import { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { buildAbility } from '../../utils/ability'
import type { Actions, Subjects } from '../../types/permissions.types'

import type { RootState } from '@/store/store'

export const usePermissions = () => {
    const { user } = useSelector((state: RootState) => state.auth)

    // Helper to extract role value from nested structure
    const getUserRole = useCallback((): string | null => {
        if (!user || !user.role) return null
        return typeof user.role === 'object' ? user.role.role : user.role
    }, [user])

    // Build ability based on user permissions
    const ability = useMemo(() => {
        if (!user) {
            return buildAbility([], false)
        }

        // Extract permissions from user role data
        const permissions = user.role?.permissions || []
        const userRole = getUserRole()

        // Update admin check for new role hierarchy
        // SECURITY CRITICAL: Only owner, admin, and hr_admin should have 'manage all' permissions
        // hr_manager should have granular permissions based on their backend permissions
        const isAdmin = userRole === 'admin' || userRole === 'owner' || userRole === 'hr_admin'

        return buildAbility(permissions, isAdmin)
    }, [user, getUserRole])

    // Helper function to check permissions with memoization
    const can = useCallback(
        (action: Actions, subject: Subjects): boolean => {
            try {
                // Simple permission check using CASL ability
                const result = ability.can(action, subject)

                return result
            } catch {
                // Fail securely - deny access on errors
                return false
            }
        },
        [ability],
    )

    // Helper function to check if user has any of the specified roles with memoization
    const hasRole = useCallback(
        (roles: string[]): boolean => {
            const userRole = getUserRole()
            if (!userRole) {
                return false
            }

            return roles.includes(userRole)
        },
        [getUserRole],
    )

    // Helper function to check if user has a specific role with memoization
    const hasExactRole = useCallback(
        (role: string): boolean => {
            const userRole = getUserRole()
            return userRole === role
        },
        [getUserRole],
    )

    // Helper function to check if user is admin (updated for new hierarchy) with memoization
    const isAdmin = useCallback((): boolean => {
        const userRole = getUserRole()
        // SECURITY CRITICAL: Only owner, admin, and hr_admin should be considered "admin"
        return userRole === 'admin' || userRole === 'owner' || userRole === 'hr_admin'
    }, [getUserRole])

    // Helper function to check if user is manager with memoization
    const isManager = useCallback((): boolean => {
        const userRole = getUserRole()
        return userRole === 'people_manager'
    }, [getUserRole])

    // Helper function to check if user has HR access with memoization
    const hasHRAccess = useCallback((): boolean => {
        const userRole = getUserRole()
        // NOTE: hasHRAccess is for UI display purposes, not permission enforcement
        // hr_manager has HR access but not admin privileges
        return userRole === 'owner' || userRole === 'admin' || userRole === 'hr_admin' || userRole === 'hr_manager'
    }, [getUserRole])

    return {
        ability,
        can,
        hasRole,
        hasExactRole,
        isAdmin,
        isManager,
        hasHRAccess,
        getUserRole,
        user,
    }
}
